import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  Upcoming: "bg-stone-100 text-stone-600 border-stone-300",
  Eligible: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Booked: "bg-sky-50 text-sky-800 border-sky-200",
  Expired: "bg-amber-50 text-amber-800 border-amber-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

function fmt(dateIso) {
  if (!dateIso) return "—";
  const [y, m, d] = dateIso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

export default function AdminTopups() {
  const [tab, setTab] = useState("active");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [treatmentFilter, setTreatmentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("original_appointment_date");
  const [emailingId, setEmailingId] = useState(null);
  const [altEmail, setAltEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/admin/topups?status=${tab}`); setRows(r.data || []); }
    catch { toast.error("Could not load top-ups"); }
    setLoading(false);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows
    .filter((r) => {
      if (treatmentFilter !== "all" && r.original_treatment_name !== treatmentFilter) return false;
      if (!q.trim()) return true;
      const s = q.trim().toLowerCase();
      return (r.client_name || "").toLowerCase().includes(s) || (r.client_email || "").toLowerCase().includes(s);
    })
    .sort((a, b) => (a[sortBy] || "").localeCompare(b[sortBy] || ""));

  const send = async (row) => {
    if (altEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(altEmail)) { toast.error("Invalid email format"); return; }
    try {
      const r = await api.post(`/admin/topups/${row.id}/send-email`, { alternative_email: altEmail || null });
      toast.success(r.data.used_alternative_email
        ? `Top-up email sent to ${r.data.delivered_to} (client's primary email unchanged)`
        : `Top-up email sent to ${r.data.delivered_to}`);
      setEmailingId(null); setAltEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const uniqTreatments = [...new Set(rows.map((r) => r.original_treatment_name))];

  return (
    <div data-testid="topups-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-4xl font-light">Top Ups</h2>
        <button onClick={load} data-testid="topups-refresh" className="mono-label border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition">Refresh</button>
      </div>
      <p className="text-sm text-stone-600 mb-6 max-w-2xl">Auto-created 14 days after every Anti-Wrinkle (1/2/3 Area) treatment is marked Completed. Clients have a 5-day window to book their top-up on the exact same treatment.</p>

      <div className="flex gap-2 mb-6">
        {[["active", "Active"], ["expired", "Expired Top Ups"]].map(([k, l]) => (
          <button key={k} data-testid={`topup-tab-${k}`} onClick={() => setTab(k)} className={`mono-label px-4 py-2 border ${tab === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 hover:border-stone-900"}`}>{l}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <input data-testid="topup-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="border hairline bg-white px-3 py-2 text-sm flex-1 min-w-[200px]" />
        <select value={treatmentFilter} onChange={(e) => setTreatmentFilter(e.target.value)} data-testid="topup-treatment-filter" className="border hairline bg-white px-3 py-2 text-sm">
          <option value="all">All treatments</option>
          {uniqTreatments.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="topup-sort" className="border hairline bg-white px-3 py-2 text-sm">
          <option value="original_appointment_date">Sort by treatment date</option>
          <option value="topup_start_date">Sort by top-up date</option>
          <option value="client_name">Sort by client</option>
        </select>
      </div>

      {loading && <p className="text-sm text-stone-500">Loading…</p>}
      {!loading && filtered.length === 0 && <p className="text-sm text-stone-500 py-12 text-center border hairline bg-white">No records.</p>}
      {!loading && filtered.length > 0 && (
        <div className="border hairline bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b hairline bg-alt">
              <tr className="mono-label text-left">
                <th className="p-3">Client</th>
                <th className="p-3">Treatment</th>
                <th className="p-3">Original Appt</th>
                <th className="p-3">Top-up Window</th>
                <th className="p-3">Reminder</th>
                <th className="p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b hairline last:border-b-0" data-testid={`topup-row-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-stone-900">{r.client_name || "—"}</div>
                    <div className="text-xs text-stone-500">{r.client_email}</div>
                    {r.client_phone && <div className="text-xs text-stone-500">{r.client_phone}</div>}
                  </td>
                  <td className="p-3 text-xs">{r.original_treatment_name}</td>
                  <td className="p-3 text-xs">{fmt(r.original_appointment_date)} · {r.original_appointment_time}</td>
                  <td className="p-3 text-xs">
                    {fmt(r.topup_start_date)} – {fmt(r.topup_end_date)}
                    {r.topup_booking_id && <div className="mt-1 text-[10px] text-sky-700">Booked: {fmt(r.topup_booking_date)} · {r.topup_booking_time}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    {r.reminder_sent_at ? (
                      <>
                        <div className="text-emerald-700">SENT</div>
                        <div className="text-stone-500 text-[10px]">to {r.reminder_delivered_to}</div>
                      </>
                    ) : <span className="text-stone-400">—</span>}
                  </td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-1 border text-[10px] ${STATUS_STYLES[r.status_computed]}`} data-testid={`topup-status-${r.id}`}>{r.status_computed.toUpperCase()}</span>
                  </td>
                  <td className="p-3 text-right">
                    {["Eligible", "Upcoming"].includes(r.status_computed) && (
                      emailingId === r.id ? (
                        <div className="flex gap-1 items-center">
                          <input data-testid={`topup-alt-email-${r.id}`} placeholder="alt email (optional)" value={altEmail} onChange={(e) => setAltEmail(e.target.value)} className="border hairline bg-white px-2 py-1 text-[11px] w-40" />
                          <button data-testid={`topup-send-confirm-${r.id}`} onClick={() => send(r)} className="mono-label text-[10px] border border-stone-900 px-2 py-1 hover:bg-stone-900 hover:text-white">Send</button>
                          <button onClick={() => { setEmailingId(null); setAltEmail(""); }} className="mono-label text-[10px] text-stone-500 px-1">✕</button>
                        </div>
                      ) : (
                        <button data-testid={`topup-email-${r.id}`} onClick={() => { setEmailingId(r.id); setAltEmail(""); }} className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white">
                          {r.reminder_sent_at ? "Resend Email" : "Send Top-Up Email"}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
